import { useMemo, useState } from 'react'
import { ModalShell, CancelButton, PrimaryButton, ServerError } from '../modal-form'
import { useImportWtt } from '../../hooks/useScheduleV2'
import { parseWtt } from '../../lib/parseWtt'
import { SAMPLE_WTT } from '../../lib/sampleWtt'
import type { Service } from '../../types'

// ImportWttModal — spec 15 §3. Paste-CSV/TSV import that replaces all
// services for the event. Calls parseWtt to validate up-front and shows
// a preview before submission.

interface Props {
  eventId: number
  existing: Service[]
  onClose: () => void
}

export function ImportWttModal({ eventId, existing, onClose }: Props) {
  const importMut = useImportWtt(eventId)
  const [raw, setRaw] = useState('')
  const [preserveCrew, setPreserveCrew] = useState(true)

  const parsed = useMemo(() => parseWtt(raw), [raw])
  const validCount = parsed.valid.length
  const errorCount = parsed.errors.length

  const canImport = validCount > 0 && errorCount === 0 && !importMut.isPending

  function handleImport() {
    if (!canImport) return
    importMut.mutate(
      {
        services: parsed.valid.map((r) => r.row),
        preserveCrew
      },
      {
        onSuccess: () => onClose()
      }
    )
  }

  return (
    <ModalShell
      title="Import working timetable"
      size="2xl"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} disabled={importMut.isPending} />
          <PrimaryButton type="button" onClick={handleImport} disabled={!canImport}>
            {importMut.isPending
              ? 'Importing…'
              : `Import ${validCount} service${validCount === 1 ? '' : 's'}`}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">
            Paste a tab- or comma-separated WTT below. The first row must be headers:{' '}
            <code className="font-mono text-xs">time, service, loco, from, to, capacity</code>.
          </p>

          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={8}
            placeholder="time,service,loco,from,to,capacity&#10;09:30,Service 1,Thomas (No.1),Pickering,Newbridge,320"
            className="w-full border border-line rounded p-3 text-xs font-mono resize-y focus:outline-none focus:border-green focus:ring-1 focus:ring-green/30"
          />

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setRaw(SAMPLE_WTT)}
              className="text-xs text-ink-soft hover:text-ink underline"
            >
              Load sample WTT
            </button>
          </div>

          {parsed.rows.length > 0 && (
            <div className="bg-parchment border border-line rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft">
                  Preview
                </span>
                <span className="text-xs font-mono">
                  <span className="text-green font-semibold">{validCount} parsed</span>
                  {errorCount > 0 && (
                    <span className="text-oxblood font-semibold ml-2">
                      {errorCount} error{errorCount === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
              </div>

              {errorCount > 0 && (
                <ul className="text-xs text-oxblood space-y-1 mb-2">
                  {parsed.errors.map((e, i) => (
                    <li key={i}>
                      <span className="font-mono">Line {e.line}:</span> {e.error}
                      {e.raw && (
                        <span className="text-ink-soft"> — {e.raw.slice(0, 60)}
                          {e.raw.length > 60 ? '…' : ''}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {validCount > 0 && (
                <div className="max-h-60 overflow-y-auto bg-white border border-line rounded">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left border-b border-line">
                        <th className="px-2 py-1 font-semibold">Time</th>
                        <th className="px-2 py-1 font-semibold">Service</th>
                        <th className="px-2 py-1 font-semibold">Loco</th>
                        <th className="px-2 py-1 font-semibold">From → To</th>
                        <th className="px-2 py-1 font-semibold text-right">Cap</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.valid.map((r, i) => (
                        <tr key={i} className="border-b border-line last:border-b-0">
                          <td className="px-2 py-1 font-mono">{r.row.time}</td>
                          <td className="px-2 py-1">{r.row.service}</td>
                          <td className="px-2 py-1">{r.row.loco}</td>
                          <td className="px-2 py-1 text-ink-soft">{r.row.from} → {r.row.to}</td>
                          <td className="px-2 py-1 text-right font-mono">{r.row.capacity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {existing.length > 0 && validCount > 0 && (
            <div className="bg-brass/10 border border-brass rounded p-3 text-sm text-ink">
              ⚠ This will replace all {existing.length} existing service
              {existing.length === 1 ? '' : 's'} for this event.
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={preserveCrew}
              onChange={(e) => setPreserveCrew(e.target.checked)}
            />
            Keep existing crew assignments where service names match
          </label>

          {importMut.isError && (
            <ServerError title="Could not import" error={importMut.error as Error} />
          )}
        </div>
      }
    />
  )
}
